import React from 'react';
import { CardStatsProps } from '@/types/components';

export const CardStats: React.FC<CardStatsProps> = ({
  title,
  value,
  icon,
  subtitle,
  trend,
  color = 'default',
  size = 'medium',
  loading = false,
  onClick,
  className = '',
}) => {
  const baseClasses =
    'bg-bg-white-0 rounded-lg border border-stroke-soft-200 transition-all duration-200';

  const interactiveClasses = onClick
    ? 'cursor-pointer hover:shadow-card-xs hover:border-stroke-sub-300'
    : '';

  const sizeClasses = {
    small: 'p-4',
    medium: 'p-6',
    large: 'p-8',
  };

  // Color variants for icons — DS state tokens
  const colorClasses = {
    default: 'text-text-soft-400',
    blue: 'text-information-base',
    green: 'text-success-base',
    yellow: 'text-away-base',
    red: 'text-error-base',
    purple: 'text-primary',
  };

  const iconSizes = {
    small: 'text-[20px]',
    medium: 'text-[24px]',
    large: 'text-[28px]',
  };

  const valueSizes = {
    small: 'text-xl',
    medium: 'text-2xl',
    large: 'text-3xl',
  };

  if (loading) {
    return (
      <div
        className={`${baseClasses} ${sizeClasses[size]} ${className}`.trim()}
      >
        <div className="animate-pulse">
          <div className="flex items-center">
            <div className="shrink-0">
              <div className="w-6 h-6 bg-bg-soft-200 rounded-sm"></div>
            </div>
            <div className="ml-4 flex-1">
              <div className="h-4 bg-bg-soft-200 rounded-sm w-3/4 mb-2"></div>
              <div className="h-8 bg-bg-soft-200 rounded-sm w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${baseClasses} ${sizeClasses[size]} ${interactiveClasses} ${className}`.trim()}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center flex-1">
          {icon && (
            <div className="shrink-0">
              <i
                className={`${icon} ${iconSizes[size]} ${colorClasses[color]}`}
              ></i>
            </div>
          )}
          <div className={icon ? 'ml-4' : ''}>
            <p className="text-sm font-medium text-text-soft-400 mb-1">{title}</p>
            <p className={`font-semibold text-text-strong-950 ${valueSizes[size]}`}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
            {subtitle && (
              <p className="text-xs text-text-soft-400 mt-1">{subtitle}</p>
            )}
          </div>
        </div>

        {trend && (
          <div className="flex items-center text-sm">
            <i
              className={`${
                trend.direction === 'up'
                  ? 'ri-arrow-up-line'
                  : 'ri-arrow-down-line'
              } text-[16px] mr-1 ${
                trend.direction === 'up' ? 'text-success-base' : 'text-error-base'
              }`}
            ></i>
            <span
              className={`font-medium ${
                trend.direction === 'up' ? 'text-success-dark' : 'text-error-dark'
              }`}
            >
              {Math.abs(trend.value)}%
            </span>
            {trend.period && (
              <span className="text-text-sub-600 ml-1">{trend.period}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CardStats;
